import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    useAutoReferralPrograms,
    useCreateAutoReferralProgram,
    useUpdateAutoReferralProgram,
    useDeleteAutoReferralProgram,
} from "../../api/autoReferral";
import {
    AutoReferralProgram,
    AutoReferralTranslationsPayload,
} from "../../types/autoReferral";

const SUPPORTED_LANGUAGES = ["ru", "en", "uk", "ka"] as const;
type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

interface TranslationFormState {
    name: string;
    short_description: string;
    brief_description: string;
    description: string;
}

interface ProgramFormState {
    translations: Record<string, TranslationFormState>;
    min_subscription_amount: string;
    max_coefficient: string;
    referral_code: string;
    sort_order: string;
    is_subscription_active: boolean;
    logo: File | null;
}

const createEmptyTranslations = (): Record<string, TranslationFormState> =>
    SUPPORTED_LANGUAGES.reduce(
        (acc, locale) => ({
            ...acc,
            [locale]: {
                name: "",
                short_description: "",
                brief_description: "",
                description: "",
            },
        }),
        {} as Record<string, TranslationFormState>
    );

const createDefaultFormState = (): ProgramFormState => ({
    translations: createEmptyTranslations(),
    min_subscription_amount: "10",
    max_coefficient: "10",
    referral_code: "",
    sort_order: "0",
    is_subscription_active: true,
    logo: null,
});

const AutoReferralProgramsList: React.FC = () => {
    const languages = useMemo<SupportedLanguage[]>(() => [...SUPPORTED_LANGUAGES], []);
    const defaultLocale = languages[0];

    const [page, setPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formState, setFormState] = useState<ProgramFormState>(() => createDefaultFormState());
    const [activeLocale, setActiveLocale] = useState<SupportedLanguage>(defaultLocale);
    const [editingProgram, setEditingProgram] = useState<AutoReferralProgram | null>(null);
    const [formError, setFormError] = useState<string | null>(null);

    const { data, isLoading, isError, error } = useAutoReferralPrograms({ page });
    const createProgram = useCreateAutoReferralProgram();
    const updateProgram = useUpdateAutoReferralProgram();
    const deleteProgram = useDeleteAutoReferralProgram();

    const programs = data?.data ?? [];
    const meta = data?.meta;

    const openCreateModal = () => {
        setEditingProgram(null);
        setFormState(createDefaultFormState());
        setActiveLocale(defaultLocale);
        setFormError(null);
        setIsModalOpen(true);
    };

    const openEditModal = (program: AutoReferralProgram) => {
        setEditingProgram(program);

        const translationsState = createEmptyTranslations();

        program.translations?.forEach((translation) => {
            if (!translation?.locale) {
                return;
            }

            const locale = translation.locale as string;

            translationsState[locale] = {
                name: translation.name ?? "",
                short_description: translation.short_description ?? "",
                brief_description: translation.brief_description ?? "",
                description: translation.description ?? "",
            };
        });

        setFormState({
            translations: translationsState,
            min_subscription_amount: program.min_subscription_amount,
            max_coefficient: String(program.max_coefficient),
            referral_code: program.referral_code,
            sort_order: String(program.sort_order),
            is_subscription_active: program.is_subscription_active,
            logo: null,
        });
        setActiveLocale(defaultLocale);
        setFormError(null);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormState(createDefaultFormState());
        setEditingProgram(null);
        setFormError(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);

        const maxCoefficientValue = Number(formState.max_coefficient);
        if (isNaN(maxCoefficientValue) || maxCoefficientValue < 1 || maxCoefficientValue > 100) {
            setFormError("Максимальный коэффициент должен быть от 1 до 100.");
            return;
        }

        const translationsPayload: AutoReferralTranslationsPayload = [];

        for (const locale of languages) {
            const current = formState.translations[locale] ?? {
                name: "",
                short_description: "",
                brief_description: "",
                description: "",
            };

            const name = current.name.trim();
            const shortDescription = current.short_description.trim();
            const briefDescription = current.brief_description.trim();
            const description = current.description.trim();

            const normalizedTranslation = {
                locale,
                name,
                short_description: shortDescription.length > 0 ? shortDescription : null,
                brief_description: briefDescription.length > 0 ? briefDescription : null,
                description: description.length > 0 ? description : null,
            };

            if (locale === defaultLocale) {
                if (name.length === 0) {
                    setFormError("Название для основного языка обязательно.");
                    setActiveLocale(locale);
                    return;
                }
                translationsPayload.push(normalizedTranslation);
                continue;
            }

            const hasContent =
                name.length > 0 || shortDescription.length > 0 || briefDescription.length > 0 || description.length > 0;

            if (!hasContent) {
                continue;
            }

            if (name.length === 0) {
                setFormError(
                    `Введите название для языка ${locale.toUpperCase()} или удалите заполненные поля.`
                );
                setActiveLocale(locale);
                return;
            }

            translationsPayload.push(normalizedTranslation);
        }

        if (!formState.referral_code.trim()) {
            setFormError("Реферальный код обязателен.");
            return;
        }

        const basePayload = {
            translations: translationsPayload,
            min_subscription_amount: formState.min_subscription_amount,
            max_coefficient: maxCoefficientValue,
            referral_code: formState.referral_code.trim(),
            sort_order:
                formState.sort_order.trim() === ""
                    ? undefined
                    : Number(formState.sort_order),
            is_subscription_active: formState.is_subscription_active,
            logo: formState.logo,
        };

        try {
            if (editingProgram) {
                await updateProgram.mutateAsync({
                    id: editingProgram.id,
                    ...basePayload,
                });
            } else {
                await createProgram.mutateAsync(basePayload);
            }
            closeModal();
        } catch (err: any) {
            setFormError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Ошибка сохранения программы."
            );
        }
    };

    const handleDelete = async (program: AutoReferralProgram) => {
        if (
            window.confirm(
                `Удалить программу "${program.name}"? Это действие необратимо.`
            )
        ) {
            try {
                await deleteProgram.mutateAsync(program.id);
            } catch (err: any) {
                window.alert(
                    err?.response?.data?.message ||
                        err?.message ||
                        "Не удалось удалить программу."
                );
            }
        }
    };

    const renderStatusBadge = (program: AutoReferralProgram) => {
        if (!program.is_subscription_active) {
            return (
                <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-red-700 bg-red-100 rounded-full">
                    Остановлена
                </span>
            );
        }

        return (
            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold text-blue-700 bg-blue-100 rounded-full">
                Активна
            </span>
        );
    };

    const modalTitle = useMemo(
        () => (editingProgram ? "Редактирование программы" : "Новая автореферальная программа"),
        [editingProgram]
    );

    const updateTranslationField = (
        locale: SupportedLanguage,
        field: keyof TranslationFormState,
        value: string
    ) => {
        setFormState((prev) => ({
            ...prev,
            translations: {
                ...prev.translations,
                [locale]: {
                    ...(prev.translations[locale] ?? {
                        name: "",
                        short_description: "",
                        brief_description: "",
                        description: "",
                    }),
                    [field]: value,
                },
            },
        }));
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    Автореферальные программы
                </h1>
                <button
                    onClick={openCreateModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg shadow hover:bg-blue-700 transition"
                >
                    Добавить программу
                </button>
            </div>

            {isLoading && (
                <div className="text-gray-500 dark:text-gray-300">
                    Загрузка программ...
                </div>
            )}

            {isError && (
                <div className="text-red-500">
                    {(error as any)?.message || "Не удалось загрузить список."}
                </div>
            )}

            {!isLoading && !isError && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-900">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Название
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Реферальный код
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Статус
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Собрано
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Участники
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Подписки
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Действия
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {programs.map((program) => (
                                <tr key={program.id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition">
                                    <td className="px-4 py-3">
                                        <div className="flex items-center space-x-3">
                                            {program.logo_url ? (
                                                <img
                                                    src={program.logo_url}
                                                    alt={program.name}
                                                    className="w-10 h-10 rounded object-cover"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500">
                                                    {program.name.slice(0, 1)}
                                                </div>
                                            )}
                                            <div>
                                                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                                    {program.name}
                                                </div>
                                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                                    Мин. сумма: {program.min_subscription_amount} USD • Макс. коэффициент:{" "}
                                                    {program.max_coefficient}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <code className="text-sm font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                                            {program.referral_code}
                                        </code>
                                    </td>
                                    <td className="px-4 py-3">
                                        {renderStatusBadge(program)}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                                        <div className="font-semibold text-gray-900 dark:text-white">
                                            {program.total_collected_amount} USD
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                                        {program.total_participants}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-200">
                                        <div>
                                            Активных:{" "}
                                            {program.active_subscriptions_count ?? 0}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            Всего: {program.total_subscriptions_count ?? 0}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right text-sm font-medium">
                                        <div className="inline-flex items-center gap-2">
                                            <Link
                                                to={`/dashboard/auto-referral-programs/${program.id}`}
                                                className="inline-flex items-center justify-center p-2 bg-indigo-500 text-white rounded hover:bg-indigo-600 transition focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                                title="Детали"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M12 5a9 9 0 100 18 9 9 0 000-18zm0 0V1m0 0l3 3m-3-3l-3 3"
                                                    />
                                                </svg>
                                            </Link>
                                            <button
                                                onClick={() => openEditModal(program)}
                                                className="inline-flex items-center justify-center p-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition focus:outline-none focus:ring-2 focus:ring-yellow-400"
                                                title="Изменить"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M15.232 5.232l3.536 3.536M4 20h4.586a1 1 0 00.707-.293l10-10a1 1 0 000-1.414l-3.586-3.586a1 1 0 00-1.414 0l-10 10A1 1 0 004 15.414V20z"
                                                    />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(program)}
                                                className="inline-flex items-center justify-center p-2 bg-red-500 text-white rounded hover:bg-red-600 transition focus:outline-none focus:ring-2 focus:ring-red-400"
                                                title="Удалить"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth={2}
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5-4h4m-5 4V4m6 0v3m5 0H4"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {programs.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="px-4 py-6 text-center text-gray-500 dark:text-gray-400"
                                    >
                                        Программы пока не созданы.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {meta && (
                <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-300">
                    <div>
                        Страница {meta.current_page} из {meta.last_page}. Всего программ:{" "}
                        {meta.total}.
                    </div>
                    <div className="space-x-2">
                        <button
                            disabled={page <= 1}
                            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                            className={`px-3 py-1 rounded border ${
                                page <= 1
                                    ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "text-gray-700 border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                            Назад
                        </button>
                        <button
                            disabled={page >= meta.last_page}
                            onClick={() =>
                                setPage((prev) => Math.min(meta.last_page, prev + 1))
                            }
                            className={`px-3 py-1 rounded border ${
                                page >= meta.last_page
                                    ? "text-gray-400 border-gray-200 cursor-not-allowed"
                                    : "text-gray-700 border-gray-300 hover:bg-gray-100"
                            }`}
                        >
                            Далее
                        </button>
                    </div>
                </div>
            )}

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto">
                        <button
                            onClick={closeModal}
                            className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-2xl font-semibold"
                            aria-label="Закрыть модальное окно"
                        >
                            ×
                        </button>
                        <h2 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                            {modalTitle}
                        </h2>
                        {formError && (
                            <div className="mb-4 text-sm text-red-500">{formError}</div>
                        )}
                        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="md:col-span-2">
                                <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
                                    <nav className="-mb-px flex flex-wrap gap-2" aria-label="Tabs">
                                        {languages.map((lng) => (
                                            <button
                                                key={lng}
                                                type="button"
                                                onClick={() => setActiveLocale(lng)}
                                                className={`whitespace-nowrap py-2 px-3 border-b-2 text-sm font-medium ${
                                                    activeLocale === lng
                                                        ? "border-blue-600 text-blue-600"
                                                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                                }`}
                                            >
                                                {lng.toUpperCase()}
                                            </button>
                                        ))}
                                    </nav>
                                </div>
                                <div className="grid grid-cols-1 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Название ({activeLocale.toUpperCase()})
                                        </label>
                                        <input
                                            type="text"
                                            value={formState.translations[activeLocale]?.name ?? ""}
                                            onChange={(e) =>
                                                updateTranslationField(activeLocale, "name", e.target.value)
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            required={activeLocale === defaultLocale}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Краткое описание ({activeLocale.toUpperCase()})
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                formState.translations[activeLocale]?.short_description ?? ""
                                            }
                                            onChange={(e) =>
                                                updateTranslationField(
                                                    activeLocale,
                                                    "short_description",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Краткое описание после ({activeLocale.toUpperCase()})
                                        </label>
                                        <input
                                            type="text"
                                            value={
                                                formState.translations[activeLocale]?.brief_description ?? ""
                                            }
                                            onChange={(e) =>
                                                updateTranslationField(
                                                    activeLocale,
                                                    "brief_description",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Полное описание ({activeLocale.toUpperCase()})
                                        </label>
                                        <textarea
                                            value={formState.translations[activeLocale]?.description ?? ""}
                                            onChange={(e) =>
                                                updateTranslationField(
                                                    activeLocale,
                                                    "description",
                                                    e.target.value
                                                )
                                            }
                                            className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 h-24"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Реферальный код
                                </label>
                                <input
                                    type="text"
                                    value={formState.referral_code}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            referral_code: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                                    required
                                    disabled={!!editingProgram}
                                />
                                {editingProgram && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Реферальный код нельзя изменить после создания
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Статус подписки
                                </label>
                                <select
                                    value={formState.is_subscription_active ? "1" : "0"}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            is_subscription_active: e.target.value === "1",
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="1">Активна</option>
                                    <option value="0">Остановлена</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Минимальная подписка (USD)
                                </label>
                                <input
                                    type="number"
                                    min="10"
                                    step="0.01"
                                    value={formState.min_subscription_amount}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            min_subscription_amount: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Максимальный коэффициент
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    max="100"
                                    value={formState.max_coefficient}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            max_coefficient: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Порядок сортировки
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={formState.sort_order}
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            sort_order: e.target.value,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Логотип
                                </label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        setFormState((prev) => ({
                                            ...prev,
                                            logo: e.target.files?.[0] ?? null,
                                        }))
                                    }
                                    className="w-full border border-gray-300 dark:border-gray-700 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div className="md:col-span-2 flex justify-end space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                                >
                                    {editingProgram ? "Сохранить" : "Создать"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoReferralProgramsList;

